import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import FocusTrap from 'focus-trap-react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../stores/store';
import { ReactComponent as CancelIcon } from './cancel.svg';
import { ReactComponent as InfoIcon } from './info-circle.svg';
import { ReactComponent as PlayIcon } from './play.svg';
import { ReactComponent as StopIcon } from './stop.svg';
import { ReactComponent as WarningIcon } from './exclamation-triangle.svg';
import { RECORDING_MAX_DURATION_SEC } from './constants';
import RecordingWaveform from './recording-waveform.component';
import {
  AudioRecordingResult,
  useAudioRecorder,
} from './use-audio-recorder.hook';
import {
  Actions,
  CancelButton,
  CheatSheet,
  CheatSheetHeader,
  CheatSheetLabel,
  CheatSheetText,
  ModalContent,
  ModalTitle,
  Overlay,
  ProcessingContent,
  ProcessingDescription,
  ProcessingSpinner,
  ProcessingTexts,
  ProcessingTitle,
  RecordButton,
  RecordingSection,
  TimerElapsed,
  TimerLimit,
  TimerRow,
} from './styles';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onRecordingComplete: (recording: AudioRecordingResult) => Promise<void>;
}

const formatDuration = (totalSeconds: number) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
    2,
    '0'
  )}`;
};

const ModalVoiceRecording = ({
  isOpen,
  onClose,
  onRecordingComplete,
}: Props) => {
  const {
    commonStore: { toastError, toastInfo },
  } = useStore();
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);
  const modalRef = useRef<HTMLDialogElement>(null);

  const handleStopped = useCallback(
    async (recording: AudioRecordingResult) => {
      setIsProcessing(true);

      try {
        await onRecordingComplete(recording);
      } catch {
        toastError(t('something_went_wrong'));
      } finally {
        onClose();
      }
    },
    [onClose, onRecordingComplete, t, toastError]
  );

  const handleWarning = useCallback(() => {
    toastInfo(t('parcel_item_ai_audio_still_active'));
  }, [t, toastInfo]);

  const handlePermissionDenied = useCallback(() => {
    toastError(t('parcel_item_ai_audio_enable_microphone_access'));
  }, [t, toastError]);

  const handleError = useCallback(() => {
    toastError(t('something_went_wrong'));
  }, [t, toastError]);

  const { status, elapsedSec, stream, isWarning, start, stop, cancel } =
    useAudioRecorder({
      onStopped: handleStopped,
      onWarning: handleWarning,
      onPermissionDenied: handlePermissionDenied,
      onError: handleError,
    });

  const isRecording = status === 'recording';
  const isStarting = status === 'requesting';

  const handleCancel = useCallback(() => {
    cancel();
    onClose();
  }, [cancel, onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isProcessing) handleCancel();
    };

    document.addEventListener('keydown', handleKeyDown);

    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = overflow;
    };
  }, [handleCancel, isOpen, isProcessing]);

  if (!isOpen) return null;

  const elapsed = formatDuration(elapsedSec);
  const limit = formatDuration(RECORDING_MAX_DURATION_SEC);

  return createPortal(
    <FocusTrap
      focusTrapOptions={{
        initialFocus: false,
        fallbackFocus: () => modalRef.current ?? document.body,
      }}>
      <Overlay
        ref={modalRef}
        open
        tabIndex={-1}
        $isProcessing={isProcessing}
        aria-modal='true'
        aria-label={
          isProcessing
            ? t('parcel_item_ai_audio_processing')
            : t('parcel_item_ai_audio_listening')
        }>
        {isProcessing ? (
          <ProcessingContent>
            <ProcessingSpinner aria-hidden='true' />
            <ProcessingTexts
              as='output'
              aria-live='polite'>
              <ProcessingTitle>
                {t('parcel_item_ai_audio_processing')}
              </ProcessingTitle>
              <ProcessingDescription>
                {t('parcel_item_ai_audio_processing_description')}
              </ProcessingDescription>
            </ProcessingTexts>
          </ProcessingContent>
        ) : (
          <ModalContent>
            <ModalTitle>{t('parcel_item_ai_audio_listening')}</ModalTitle>

            <RecordingSection>
              <RecordingWaveform
                isRecording={isRecording}
                isWarning={isWarning}
                stream={stream}
              />

              <TimerRow>
                <TimerElapsed
                  $isWarning={isWarning}
                  aria-live='polite'
                  aria-label={t('parcel_item_ai_audio_time_elapsed', {
                    time: elapsed,
                  })}>
                  {elapsed}
                </TimerElapsed>
                <TimerLimit aria-hidden='true'>/</TimerLimit>
                <TimerLimit
                  aria-label={t('parcel_item_ai_audio_time_limit', {
                    time: limit,
                  })}>
                  {limit}
                </TimerLimit>
              </TimerRow>

              <CheatSheet $isWarning={isWarning}>
                <CheatSheetHeader $isWarning={isWarning}>
                  {isWarning ? <WarningIcon /> : <InfoIcon />}
                  <CheatSheetLabel $isWarning={isWarning}>
                    {isWarning
                      ? t('parcel_item_ai_audio_are_you_still_dictating')
                      : t('parcel_item_ai_audio_cheat_sheet')}
                  </CheatSheetLabel>
                </CheatSheetHeader>
                <CheatSheetText>
                  {t('parcel_item_ai_audio_cheat_sheet_example')}
                </CheatSheetText>
              </CheatSheet>
            </RecordingSection>

            <Actions>
              <CancelButton
                appearance='secondary-filled'
                fullWidth={false}
                fullWidthMobile={false}
                aria-label={t('cancel')}
                onClick={handleCancel}>
                <CancelIcon aria-hidden='true' />
                <span className='cancel-label'>{t('cancel')}</span>
              </CancelButton>

              <RecordButton
                appearance='primary'
                fullWidth={false}
                fullWidthMobile={false}
                disabled={isStarting}
                $isRecording={isRecording}
                onClick={isRecording ? stop : start}>
                {isRecording ? (
                  <StopIcon aria-hidden='true' />
                ) : (
                  <PlayIcon aria-hidden='true' />
                )}
                {isRecording
                  ? t('parcel_item_ai_audio_stop_recording')
                  : t('parcel_item_ai_audio_start_recording')}
              </RecordButton>
            </Actions>
          </ModalContent>
        )}
      </Overlay>
    </FocusTrap>,
    document.body
  );
};

export default ModalVoiceRecording;
